import type React from 'react'
import { useRef } from 'react'
import { NodeAnchorMarkers } from './components/node-anchor-markers'
import { NodeHandleDots } from './components/node-handle-dots'
import { PenEditCaptureRect } from './components/pen-edit-capture-rect'
import { PenHoverHighlight } from './components/pen-hover-highlight'
import { PhotoFillGhost } from './components/photo-fill-ghost'
import { useModifierKeys } from './hooks/use-modifier-keys'
import { useNodeDragHandlers } from './hooks/use-node-drag-handlers'
import { useNodeEditPropRefs } from './hooks/use-node-edit-prop-refs'
import { useNodePathState } from './hooks/use-node-path-state'
import { usePenEditHover } from './hooks/use-pen-edit-hover'
import { usePenEditMode } from './hooks/use-pen-edit-mode'
import type { NodeEditOverlayProps } from './types/node-edit-overlay-props'

export const NodeEditOverlay: React.FC<NodeEditOverlayProps> = ({
  shape,
  bleedWidth,
  bleedHeight,
  scale,
  onPathChange,
  onPathCommit,
  geometryInteractionDisabled = false,
  isPenEditMode = false,
  onContinuePath,
  onNodeAdded,
  onPenHoverChange,
  onBackgroundDoubleClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null)

  const propRefs = useNodeEditPropRefs({
    shape,
    geometryInteractionDisabled,
    onPenHoverChange,
    onContinuePath,
    onNodeAdded,
    onPathChange,
    onPathCommit,
  })
  // Effect order (must not change): modifier keys → pen-edit mode reset (path state, which also
  // re-parses an external path change during render) → pen-edit window listeners.
  const modifierKeys = useModifierKeys()
  const path = useNodePathState(shape, isPenEditMode)
  const { handleElementDown, handleElementMove, handleElementUp } = useNodeDragHandlers({
    svgRef,
    path,
    propRefs,
    modifierKeys,
    shapeId: shape.id,
    bleedWidth,
    bleedHeight,
    geometryInteractionDisabled,
    onPathChange,
    onPathCommit,
  })
  const hover = usePenEditHover({
    svgRef,
    path,
    propRefs,
    shapeX: shape.x,
    shapeY: shape.y,
    scale,
    bleedWidth,
    bleedHeight,
  })
  const { handlePenEditClick } = usePenEditMode({
    isPenEditMode,
    shape,
    geometryInteractionDisabled,
    path,
    propRefs,
    hover,
    onPathChange,
    onPathCommit,
    onContinuePath,
    onNodeAdded,
  })

  const sw = 1 / scale
  const anchorHalf = 6 / scale
  const handleR = 4 / scale
  const handleStrokeWidth = 1.5 / scale

  const geom = path.shapeGeomRef.current
  const ox = geom.x
  const oy = geom.y

  const points = path.pointsRef.current

  if (geometryInteractionDisabled) {
    return null
  }

  const rotation = shape.rotation ?? 0
  const rotCx = ox + geom.width / 2
  const rotCy = oy + geom.height / 2
  const rotTransform = rotation ? `rotate(${rotation}, ${rotCx}, ${rotCy})` : undefined

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 select-none"
      style={{
        width: bleedWidth,
        height: bleedHeight,
        overflow: 'visible',
        zIndex: 55,
        pointerEvents: 'none',
      }}
    >
      {/* All node geometry is wrapped in a rotation group to match the CSS rotate() applied
          to the shape element by shape-renderer (transform-origin: 50% 50% = shape center). */}
      <g transform={rotTransform}>
        {shape.photoFill && (
          <PhotoFillGhost
            photoFill={shape.photoFill}
            ox={ox}
            oy={oy}
            width={geom.width}
            height={geom.height}
          />
        )}
        <NodeHandleDots
          points={points}
          ox={ox}
          oy={oy}
          sw={sw}
          handleR={handleR}
          handleStrokeWidth={handleStrokeWidth}
          isPenEditMode={isPenEditMode}
          onElementDown={handleElementDown}
          onElementMove={handleElementMove}
          onElementUp={handleElementUp}
        />

        <NodeAnchorMarkers
          points={points}
          ox={ox}
          oy={oy}
          anchorHalf={anchorHalf}
          sw={sw}
          multiSelectActive={path.multiSelectActiveRef.current}
          selectedIndices={path.selectedIndicesRef.current}
          isPenEditMode={isPenEditMode}
          onElementDown={handleElementDown}
          onElementMove={handleElementMove}
          onElementUp={handleElementUp}
        />

        {isPenEditMode && (
          <PenHoverHighlight
            penHover={hover.penHover}
            points={points}
            ox={ox}
            oy={oy}
            anchorHalf={anchorHalf}
            sw={sw}
          />
        )}
      </g>
      {/* end rotation group */}

      {isPenEditMode && (
        <PenEditCaptureRect
          bleedWidth={bleedWidth}
          bleedHeight={bleedHeight}
          penHover={hover.penHover}
          penHoverRef={hover.penHoverRef}
          onPenEditMove={hover.handlePenEditMove}
          onPenEditLeave={hover.handlePenEditLeave}
          onPenEditClick={handlePenEditClick}
          onBackgroundDoubleClick={onBackgroundDoubleClick}
        />
      )}
    </svg>
  )
}
